/**
 * components/TranslatrUI.tsx - Translatr UI Component
 * 
 * CHANGE HISTORY:
 * - 251109_0805 [pending] danielbeer: Moved from Interfacr, now uses Translatr core functions
 * - 251103_1116 [pending] danielbeer: Comprehensive Transformr with plugins and Mappr integration
 * 
 * React UI for Translatr data conversion:
 * - Dual-panel editor (source/target)
 * - Auto-format detection using Translatr
 * - Multiple display modes (raw, formatted, inspector)
 * - Syntax highlighting
 * - Plugin system for custom formats
 * - Mappr integration for field mapping
 * - Uses Translatr core functions for all conversions
 */

import React, { useCallback } from 'react';
import { TranslatrPanel } from './TranslatrPanel';
import { TranslatrToolbar } from './TranslatrToolbar';
import { MapprModal } from './MapprModal';
import { useTranslatr } from '../hooks/useTranslatr';
import type { TranslatrProps, DataFormat, DisplayMode } from '../types';
import './TranslatrUI.css';

/**
 * TranslatrUI - Universal data transformation UI component
 * 
 * @example
 * ```tsx
 * <TranslatrUI
 *   initialSource={csvData}
 *   initialSourceFormat="csv"
 *   initialTargetFormat="json"
 *   autoTransform={true}
 *   showMapprButton={true}
 * />
 * ```
 */
export const TranslatrUI: React.FC<TranslatrProps> = ({
  initialSource = '',
  initialSourceFormat = 'auto',
  initialTargetFormat = 'json',
  onTransform,
  onChange,
  autoTransform = false,
  debounceDelay = 500,
  theme = 'light',
  showToolbar = true,
  showFormatSelectors = true,
  showDisplayModes = true,
  showMapprButton = true,
  layout = 'horizontal',
  className = '',
  translatrManager,
  distillr,
  profylrContext,
  plugins = []
}) => {
  // Use Translatr hook for state management
  const translatr = useTranslatr({
    initialSource,
    initialSourceFormat,
    initialTargetFormat,
    onTransform,
    onChange,
    autoTransform,
    debounceDelay,
    plugins
  });

  // Handle source content change
  const handleSourceChange = useCallback((content: string) => {
    translatr.setSourceContent(content);
  }, [translatr]);

  // Handle source format change
  const handleSourceFormatChange = useCallback((format: DataFormat) => {
    translatr.setSourceFormat(format);
  }, [translatr]);

  // Handle source display mode change
  const handleSourceDisplayModeChange = useCallback((mode: DisplayMode) => {
    translatr.setSourceDisplayMode(mode);
  }, [translatr]);

  // Handle target format change
  const handleTargetFormatChange = useCallback(async (format: DataFormat) => {
    translatr.setTargetFormat(format);
    // Auto-transform when target format changes
    if (translatr.source.content) {
      setTimeout(() => translatr.transform(), 100);
    }
  }, [translatr]);

  // Handle target display mode change
  const handleTargetDisplayModeChange = useCallback((mode: DisplayMode) => {
    translatr.setTargetDisplayMode(mode);
  }, [translatr]);

  // Handle transform button click
  const handleTransform = useCallback(async () => {
    await translatr.transform();
  }, [translatr]);

  // Handle distill button click
  const handleDistill = useCallback(async () => {
    // Distill: auto-detect source format and extract structured data
    // Set source to auto-detect if not already
    if (translatr.source.format !== 'auto') {
      translatr.setSourceFormat('auto');
      // Wait for state update before transforming
      setTimeout(() => translatr.transform(), 50);
    } else {
      // Already in auto mode, just transform
      await translatr.transform();
    }
  }, [translatr]);

  // Handle Mappr button click
  const handleOpenMappr = useCallback(() => {
    translatr.openMappr();
  }, [translatr]);

  // Handle Mappr close
  const handleCloseMappr = useCallback(() => {
    translatr.closeMappr();
  }, [translatr]);

  // Handle Mappr mappings apply
  const handleApplyMappings = useCallback((mappings: any[]) => {
    translatr.applyMappings(mappings);
  }, [translatr]);

  // Get available formats
  const availableFormats: DataFormat[] = [
    'auto',
    'json',
    'csv',
    'tsv',
    'xml',
    'yaml',
    'markdown',
    'html',
    'text',
    'vcard',
    'icalendar',
    'contact',
    ...(translatr.plugins ? Array.from(translatr.plugins.keys()).filter(f => 
      !['auto', 'json', 'csv', 'tsv', 'xml', 'yaml', 'markdown', 'html', 'text', 'vcard', 'icalendar', 'contact'].includes(f)
    ) : [])
  ];

  return (
    <div 
      className={`translatr translatr--${theme} translatr--${layout} ${className}`}
      data-testid="translatr"
    >
      {/* Toolbar - now only contains utility buttons, Transform/Distill moved to divider */}
      {showToolbar && (
        <TranslatrToolbar
          onClear={translatr.clear}
          onLoadSample={(sampleId, content) => {
            translatr.setSourceContent(content);
            // Auto-transform after loading sample
            setTimeout(() => translatr.transform(), 100);
          }}
          onCopySource={() => translatr.copyToClipboard(translatr.source.content)}
          onCopyTarget={() => translatr.copyToClipboard(translatr.target.content)}
          onOpenMappr={showMapprButton ? handleOpenMappr : undefined}
          isTransforming={translatr.isTransforming}
          lastResult={translatr.lastResult}
          theme={theme}
        />
      )}

      {/* Main content area */}
      <div className="translatr__content">
        {/* Source panel */}
        <TranslatrPanel
          type="source"
          config={translatr.source}
          availableFormats={availableFormats}
          onContentChange={translatr.setSourceContent}
          onFormatChange={translatr.setSourceFormat}
          onDisplayModeChange={translatr.setSourceDisplayMode}
          showFormatSelector={showFormatSelectors}
          showDisplayModes={showDisplayModes}
          theme={theme}
          onLoadSample={(sampleId, content) => {
            translatr.setSourceContent(content);
            setTimeout(() => translatr.transform(), 100);
          }}
          onClear={translatr.clear}
        />

        {/* Divider with Transform/Distill buttons */}
        <div className="translatr__divider">
          <div className="translatr__action-buttons">
            <button 
              className="translatr__transform-button"
              onClick={handleTransform}
              disabled={translatr.isTransforming || !translatr.source.content}
              title="Transform: Convert from selected source format to target format"
            >
              {translatr.isTransforming ? '⏳' : '🔄'} Transform
            </button>
            <button 
              className="translatr__distill-button"
              onClick={handleDistill}
              disabled={translatr.isTransforming || !translatr.source.content}
              title="Distill: Auto-detect source format and extract structured data (e.g., SVGs from HTML, contacts from vCard)"
            >
              {translatr.isTransforming ? '⏳' : '🧪'} Distill
            </button>
          </div>
        </div>

        {/* Target panel */}
        <TranslatrPanel
          type="target"
          config={translatr.target}
          availableFormats={availableFormats.filter(f => f !== 'auto')}
          onContentChange={(content) => {}}
          onFormatChange={translatr.setTargetFormat}
          onDisplayModeChange={translatr.setTargetDisplayMode}
          showFormatSelector={showFormatSelectors}
          showDisplayModes={showDisplayModes}
          readOnly={true}
          theme={theme}
        />
      </div>

      {/* Status bar */}
      {translatr.lastResult && (
        <div className={`translatr__status translatr__status--${translatr.lastResult.success ? 'success' : 'error'}`}>
          {translatr.lastResult.success ? (
            <>
              ✅ Transformed successfully
              {translatr.lastResult.metadata && (
                <span className="translatr__metadata">
                  {translatr.lastResult.metadata.recordCount && (
                    <span>{translatr.lastResult.metadata.recordCount} records</span>
                  )}
                  {translatr.lastResult.metadata.transformTime && (
                    <span>{translatr.lastResult.metadata.transformTime}ms</span>
                  )}
                </span>
              )}
            </>
          ) : (
            <>
              ❌ {translatr.lastResult.error}
            </>
          )}
        </div>
      )}

      {/* Mappr modal */}
      {translatr.showMappr && (
        <MapprModal
          sourceSchema={translatr.sourceSchema}
          targetSchema={translatr.targetSchema}
          onApply={handleApplyMappings}
          onClose={handleCloseMappr}
          theme={theme}
        />
      )}
    </div>
  );
};

export default TranslatrUI;
